import { navigate } from "gatsby"
import { useAtom } from "jotai"
import React, { ReactElement, useState } from "react"
import { v4 as uuid } from "uuid"
import { Parking } from "../../__generated__/proto/itech/motorist/pksha/v1/parking"
import { Transaction as ProtoTransaction } from "../../__generated__/proto/itech/motorist/pksha/v1/transaction"
import { authAtom } from "../../atoms"
import { Transaction } from "../../domain/models"
import { ParkingService } from "../../domain/services"
import { ReserveUseCase } from "../../domain/use-cases"
import { Dialog } from "../common/dialog"
import * as styles from "./parking-reservation-form.module.scss"

const parkingService = new ParkingService()
const reserveUseCase = new ReserveUseCase(parkingService)

const LicenseNumberInput = ({
  value,
  onChange,
  error,
  onValidate,
}: {
  value: string
  onChange: (value: string) => void
  error?: string
  onValidate: (error: string | null) => void
}): ReactElement => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    onChange(newValue)
    onValidate(validateLicenseNumber(newValue))
  }

  const validateLicenseNumber = (number: string): string | null => {
    if (number === "") return "車両ナンバーを入力してください。"
    if (!/^\d+$/.test(number)) return "車両ナンバーは整数でなければなりません。"
    return null
  }

  return (
    <div className={styles.textInput}>
      <div className={styles.infoKeySection}>車両ナンバー（必須）</div>
      <div className={styles.infoValueSection}>
        <input
          type="text"
          className={styles.licenseNumberInput}
          placeholder="例）1234"
          value={value}
          onChange={handleChange}
        />
        {error && <div className={styles.error}>{error}</div>}
        <div className={styles.notice}>
          駐車車両とご予約車の確認のために、4桁の車両ナンバー(数字のみ)の入力をお願いします。
        </div>
      </div>
    </div>
  )
}

const SpaceNumberInput = ({
  value,
  onChange,
  error,
  onValidate,
}: {
  value: string
  onChange: (value: string) => void
  error?: string
  onValidate: (error: string | null) => void
}): ReactElement => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    onChange(newValue)
    onValidate(validateSpaceNumber(newValue))
  }

  const validateSpaceNumber = (number: string): string | null => {
    if (number === "") return null
    if (!/^\d+$/.test(number)) return "車室番号は整数でなければなりません。"
    return null
  }

  return (
    <div className={styles.textInput}>
      <div className={styles.infoKeySection}>車室番号（任意）</div>
      <div className={styles.infoValueSection}>
        <input
          type="text"
          className={styles.spaceNumberInput}
          placeholder="例）3"
          value={value}
          onChange={handleChange}
        />
        {error && <div className={styles.error}>{error}</div>}
        <div className={styles.notice}>
          車室を指定された場合、他の車室のご利用はできません。
        </div>
      </div>
    </div>
  )
}

const HandicappedSpaceCheckbox = ({
  checked,
  onChange,
}: {
  checked: boolean
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}): ReactElement => {
  return (
    <div className={styles.handicappedSpaceCheckbox}>
      <div className={styles.infoKeySection}></div>
      <div className={styles.infoValueSection}>
        <div className={styles.checkboxRow}>
          <input
            type="checkbox"
            id="requireHandicappedSpace"
            checked={checked}
            onChange={onChange}
          />
          <label htmlFor="requireHandicappedSpace">
            車椅子対応車室を優先して予約する
          </label>
        </div>
      </div>
    </div>
  )
}

interface Props {
  parking: Parking
}

export const ParkingReservationForm = React.memo(
  function ParkingReservationInfoTable(props: Props): ReactElement {
    const [token] = useAtom(authAtom)
    const [spaceNumber, setSpaceNumber] = useState("")
    const [licenseNumber, setLicenseNumber] = useState("")
    const [requireHandicappedSpace, setRequireHandicappedSpace] =
      useState(false)
    const [errors, setErrors] = useState<{
      spaceNumber?: string
      licenseNumber?: string
    }>({})
    const [reservedTime, setReservedTime] = useState<Date>()
    const [reservationError, setReservationError] = useState<string>()

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      if (!token) {
        alert("ログインしてください。")
        await navigate("/signin")
        return
      }
      if (errors.spaceNumber || errors.licenseNumber) {
        return
      }

      const paddedSpaceNumber = spaceNumber ? spaceNumber.padStart(3, "0") : ""
      const paddedLicenseNumber = licenseNumber.padStart(4, "0")

      reserveUseCase
        .call({
          token: token,
          requestId: uuid(),
          parkingSpaceName: `${props.parking.name}/spaces/${paddedSpaceNumber}`,
          licenseNumber: paddedLicenseNumber,
          requireHandicappedSpace: requireHandicappedSpace,
        })
        .then(response => {
          const transaction = Transaction.fromProto(
            response?.transaction as ProtoTransaction
          )

          if (transaction.reserved?.createTime != null) {
            setReservedTime(new Date(transaction.reserved.createTime))
          }
        })
        .catch(() => {
          setReservationError(
            "車室に空きがなく予約することができませんでした。\n駐車場検索画面から空き状況を確認いただき、再度ご予約ください。"
          )
        })
    }

    return (
      <>
        <form className={styles.container} onSubmit={handleSubmit}>
          <div className={styles.flexbox}>
            <LicenseNumberInput
              value={licenseNumber}
              onChange={value => {
                setLicenseNumber(value)
              }}
              error={errors.licenseNumber}
              onValidate={error =>
                setErrors({ ...errors, licenseNumber: error ?? undefined })
              }
            />
            <SpaceNumberInput
              value={spaceNumber}
              onChange={value => {
                setSpaceNumber(value)
              }}
              error={errors.spaceNumber}
              onValidate={error =>
                setErrors({ ...errors, spaceNumber: error ?? undefined })
              }
            />
          </div>
          <HandicappedSpaceCheckbox
            checked={requireHandicappedSpace}
            onChange={e => setRequireHandicappedSpace(e.target.checked)}
          />
          <p className={styles.importantNoticeText}>
            ※ご予約を開始された後、予約完了画面を必ずご確認ください。
          </p>
          <div className={styles.buttonRow}>
            <button type="submit">予約する</button>
          </div>
        </form>
        <Dialog
          open={reservedTime != null}
          title="ご予約が完了しました"
          confirmText="OK"
          onClose={async () => await navigate("/reservation-detail")}
        >
          <div className={styles.dialogDescription}>
            <ul>
              <li>
                予約した時点から料金が発生します。予約開始から入庫までは最大料金が適用されません。
              </li>
              <li>以下の予約開始時刻より2時間まで予約しました。</li>
            </ul>
            <p className={styles.reservationTimeTitle}>ご予約開始時刻</p>
            <p>
              {new Intl.DateTimeFormat("ja-JP", {
                month: "long",
                day: "numeric",
                hour: "numeric",
                minute: "numeric",
              }).format(reservedTime)}
            </p>
          </div>
        </Dialog>
        <Dialog
          open={reservationError != null}
          title="予約エラー"
          confirmText="OK"
          onClose={() => setReservationError(undefined)}
        >
          <div>
            {reservationError?.split("\n").map((line, index) => {
              return <p key={index}>{line}</p>
            })}
          </div>
        </Dialog>
      </>
    )
  }
)
